import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { ShortcutsService } from './shared/services/shortcuts.service';
import { servicingTransferOrderLookup } from './features/products/servicing-transfer/order-lookup';
import { ActivatedRouteSnapshot, provideRouter, RouteReuseStrategy, withViewTransitions } from '@angular/router';
import { RemountRouteReuseStrategy } from './remount-route-reuse.strategy';

import { routes } from './app.routes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { MatIconRegistry } from '@angular/material/icon';
import { AppInterceptor } from './app.interceptor';
import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication
} from '@azure/msal-browser';
import {
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MsalInterceptor,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalService
} from '@azure/msal-angular';
import { environment } from '../environments/environment';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.msalConfig.auth.authority,
      knownAuthorities: environment.msalConfig.auth.knownAuthorities,
      redirectUri: '/',
      postLogoutRedirectUri: '/'
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage
    },
    system: {
      allowPlatformBroker: false,
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info, // LogLevel.Warning
        piiLoggingEnabled: false
      }
    }
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  environment.apiConfig.forEach((x) => protectedResourceMap.set(x.uri, x.scopes));

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    loginFailedRoute: '/login-failed'
  };
}
import { DefaultGlobalConfig, provideToastr, TOAST_CONFIG } from 'ngx-toastr';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { NumberFormatPaginatorIntl } from './shared/services/number-format-paginator-intl';
import { provideTableEnhancements } from './shared/utils';

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      withViewTransitions({
        skipInitialTransition: true,
        // Only a change of page animates; query-param updates (filters, paging, tabs) swap instantly.
        onViewTransitionCreated: ({ transition, from, to }) => {
          if (routePath(from) === routePath(to) || isOverlayRoute(from) || isOverlayRoute(to)) {
            transition.skipTransition();
          }
        }
      })
    ),
    { provide: RouteReuseStrategy, useClass: RemountRouteReuseStrategy },
    // Command palette (Ctrl K): a typed number finds a Servicing Transfer order.
    provideAppInitializer(() => inject(ShortcutsService).registerLookup(servicingTransferOrderLookup())),
    { provide: MatPaginatorIntl, useClass: NumberFormatPaginatorIntl },
    provideTableEnhancements(),
    provideAnimationsAsync(),
    provideZonelessChangeDetection(),
    provideNativeDateAdapter(),
    provideHttpClient(withInterceptorsFromDi()),
    provideToastr(),
    MatIconRegistry,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'never',
        subscriptSizing: 'dynamic'
      }
    },
    {
      // The shared dialog look (styles/dialogs.scss). A dialog opened with its own panelClass replaces it.
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { panelClass: 'st-dialog' }
    },
    {
      provide: TOAST_CONFIG,
      useValue: {
        default: DefaultGlobalConfig,
        config: DefaultGlobalConfig
      }
    },
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService
  ]
};

/** Full path of a route snapshot, without query params. */
function routePath(snapshot: ActivatedRouteSnapshot): string {
  const segments: string[] = [];
  for (let route: ActivatedRouteSnapshot | null = snapshot; route; route = route.firstChild) {
    segments.push(...route.url.map((segment) => segment.path));
  }
  return segments.join('/');
}

/** A route that opens over the current page (route data `overlay: true`, e.g. the New order dialog). */
function isOverlayRoute(snapshot: ActivatedRouteSnapshot): boolean {
  for (let route: ActivatedRouteSnapshot | null = snapshot; route; route = route.firstChild) {
    if (route.data['overlay']) return true;
  }
  return false;
}
